import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ProductDrawerForm, SectionHeader } from '@/pages/business/components'
import { CatalogProcessorDrawerForm } from '@/pages/catalog/processor/list/components'
import { Alert, Skeleton } from '@mercai/clever'
import { QuotationRequestProductsBlock } from './components/products-block'

import { deepCopy, useParamsState } from '@/helpers'
import {
  QUERIES_ADMIN,
  useCatalogCategoryList,
  useUpdateQuotationRequest,
} from '@/services'

import {
  CatalogProductProps,
  QuotationRequestItemProps,
  QuotationRequestProps,
  QuotationRequestStatusEnum,
} from '@/types'
import styles from './quotation-request-products.module.css'
import { QuotationRequestProductsProps } from './quotation-request-products.type'

export const QuotationRequestProducts = ({
  position,
  validation,
  isEditable,
  catalogProducts,
  quotationRequest,
  errors,
}: QuotationRequestProductsProps) => {
  const { t } = useTranslation('screenBusinessV2')
  const queryClient = useQueryClient()

  const [queryParams, setQueryParams] = useParamsState({
    form: '',
  })

  const [productToUpdate, setProductToUpdate] = useState<
    Partial<QuotationRequestItemProps> | undefined
  >()

  const [productToCreate, setProductToCreate] = useState<
    Partial<QuotationRequestItemProps> | undefined
  >()

  const { data: catalogCategoryList } = useCatalogCategoryList()

  const { mutateAsync: mutateAsyncUpdateQuotationRequest } =
    useUpdateQuotationRequest(quotationRequest?.uuid || '')

  const onHandleRfqRemoveCatalogProduct = async (
    product: Partial<QuotationRequestItemProps>,
  ): Promise<boolean> => {
    const quotationRequestAux = deepCopy(
      quotationRequest,
    ) as QuotationRequestProps

    const newItems = quotationRequestAux.items.filter(
      (item) => item.uuid !== product.uuid,
    )

    const response = await mutateAsyncUpdateQuotationRequest({
      ...quotationRequestAux,
      items: newItems,
    })

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.QUOTATION_REQUEST.module],
      })
      setQueryParams({ form: '' })
      return true
    }

    return false
  }

  const onHandleUpdateProduct = async () => {
    const quotationRequestAux = deepCopy(
      quotationRequest,
    ) as QuotationRequestProps

    const selectedProductIndex = quotationRequestAux.items.findIndex(
      (item) => item.uuid === productToUpdate?.uuid,
    )

    quotationRequestAux.items[selectedProductIndex] = {
      ...quotationRequestAux?.items?.[selectedProductIndex],
      ...productToUpdate,
      uuid: quotationRequestAux?.items?.[selectedProductIndex]?.uuid,
      catalogProductUuid: productToUpdate?.catalogProduct?.uuid,
    }

    const response = await mutateAsyncUpdateQuotationRequest({
      ...quotationRequestAux,
    })

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [
          QUERIES_ADMIN.QUOTATION_REQUEST.module,
          quotationRequest?.uuid,
        ],
      })
      setQueryParams({ form: '' })
    }
  }

  const onHandleAddProduct = async () => {
    const quotationRequestAux = deepCopy(
      quotationRequest,
    ) as QuotationRequestProps

    quotationRequestAux.items.push({
      ...productToCreate,
      catalogProductUuid: productToCreate?.catalogProduct?.uuid,
    } as QuotationRequestItemProps)

    const response = await mutateAsyncUpdateQuotationRequest({
      ...quotationRequestAux,
    })

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [
          QUERIES_ADMIN.QUOTATION_REQUEST.module,
          quotationRequest?.uuid,
        ],
      })
      setQueryParams({ form: '' })
    }
  }

  const onHandleAddCatalogProduct = async (
    catalogProduct?: CatalogProductProps,
  ) => {
    const quotationRequestAux = deepCopy(
      quotationRequest,
    ) as QuotationRequestProps

    // Removing old non-cataloged product from the rfq
    const newItems = quotationRequestAux.items.filter(
      (item) => item.uuid !== productToUpdate?.uuid,
    )

    // Adding new cataloged product to the rfq
    const newParsedProduct = {
      uuid: productToUpdate?.uuid,
      product: {
        title: catalogProduct?.attributes.title,
        category: catalogProduct?.category.name,
        size: catalogProduct?.attributes.size,
      },
      catalogProductUuid: catalogProduct?.uuid,
      catalogProduct,
    }

    newItems.push(newParsedProduct)

    const response = await mutateAsyncUpdateQuotationRequest({
      ...quotationRequestAux,
      items: newItems,
    })

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [
          QUERIES_ADMIN.QUOTATION_REQUEST.module,
          quotationRequest?.uuid,
        ],
      })
      setQueryParams({ form: '' })
    }
  }

  const withCatalogProduct =
    catalogProducts?.filter((item) => item.catalogProduct !== null) || []

  const withoutCatalogProduct =
    catalogProducts?.filter((item) => item.catalogProduct === null) || []

  const isLoading =
    quotationRequest?.status === QuotationRequestStatusEnum.draft

  return (
    <SectionHeader
      title={`${position}. ${t('sectionCatalogProductsTitle')}`}
      key="catalog-products"
      isComplete={isEditable ? validation?.isComplete : undefined}
    >
      <div className={styles.core}>
        {isLoading && (
          <>
            <Alert variant="warning">
              {t('sectionCatalogProductsLoadingProductsAlert')}
            </Alert>

            <div className={styles['row-buttons']}>
              <Skeleton width="10%" height="2rem" />
              <Skeleton width="10%" height="2rem" />
              <Skeleton width="10%" height="2rem" />
            </div>

            <div className={styles['product-loading']}>
              <Skeleton width="100%" height="6rem" />
            </div>
          </>
        )}

        {withoutCatalogProduct.length > 0 && (
          <QuotationRequestProductsBlock
            title={t('catalogNonCatalogedProducts')}
            type="non-cataloged-product"
            rfqProducts={catalogProducts}
            errors={errors}
            isEditable={isEditable}
            catalogProducts={withoutCatalogProduct}
            queryParams={queryParams}
            onHandleRfqRemoveCatalogProduct={onHandleRfqRemoveCatalogProduct}
            setProductToUpdate={setProductToUpdate}
            setQueryParams={setQueryParams}
          />
        )}

        {withCatalogProduct.length > 0 && (
          <QuotationRequestProductsBlock
            title={t('catalogCatalogedProducts')}
            type="cataloged-product"
            rfqProducts={catalogProducts}
            errors={errors}
            isEditable={isEditable}
            catalogProducts={withCatalogProduct}
            queryParams={queryParams}
            onHandleRfqRemoveCatalogProduct={onHandleRfqRemoveCatalogProduct}
            setProductToUpdate={setProductToUpdate}
            setQueryParams={setQueryParams}
          />
        )}

        <ProductDrawerForm
          isOpen={queryParams?.form === 'select-catalog-product'}
          onHandleOpen={() => setQueryParams({ ...queryParams, form: '' })}
          catalogCategoryList={catalogCategoryList?.data?.results || []}
          values={productToUpdate as Partial<QuotationRequestItemProps>}
          onChangeValues={(values) => setProductToUpdate(values)}
          onHandleSubmit={onHandleUpdateProduct}
        />

        <ProductDrawerForm
          isOpen={queryParams?.form === 'add-catalog-product'}
          onHandleOpen={() => setQueryParams({ ...queryParams, form: '' })}
          catalogCategoryList={catalogCategoryList?.data?.results || []}
          values={productToCreate as Partial<QuotationRequestItemProps>}
          onChangeValues={(values) => setProductToCreate(values)}
          onHandleSubmit={onHandleAddProduct}
        />

        <CatalogProcessorDrawerForm
          isOpen={queryParams?.form === 'add-product-catalog'}
          onHandleOpen={() => {
            setQueryParams({ ...queryParams, form: '', uuid: '' })
          }}
          onSuccess={onHandleAddCatalogProduct}
          registerUuid={queryParams.uuid}
        />
      </div>
    </SectionHeader>
  )
}
